import { UserWorkoutSession } from 'fitbeat.gym-core';
import { IHeartRate } from 'fitbeat.models';
import { Logger } from '../utils';
import { allSupportedConnectsAppsInPriorityOrder } from './all-apps';
import ConnectedWorkoutApp, {
  AuthorizationStatus,
  IPrepareHomeWorkout,
  IPrepareStudioWorkout,
  SupportedConnectedApp,
  UnRegisterEventListener,
} from './connected-workout-app';
import { pollyfilledAllSettled } from '../utils/promise-utils';

type HRDataListener = (data: IHeartRate) => void;

async function initializeAppIfAvailable(app: ConnectedWorkoutApp): Promise<boolean> {
  try {
    const isAvailable = await app.isAvailable();
    if (!isAvailable) {
      Logger.debug(`ConnectedApps: ${app.name} is not available`);
      return false;
    }

    await app.initialize();
    Logger.debug(`ConnectedApps: ${app.name} successfully initialized`);

    return true;
  } catch (err: unknown) {
    Logger.notify(new Error(`Failed to initialize ${app.name}: ${err}`));
    return false;
  }
}

export interface IAppForOnboarding {
  app: SupportedConnectedApp;
  isConnectedDeviceAvailable?: boolean;
  isAppInstalledOnConnectedDevice?: boolean;
  isAppInstalled: boolean;
}

class ConnectedAppsManagerSingleton {
  public readonly connectedApps: ConnectedWorkoutApp[] = [];

  private hrDataUnRegisters: UnRegisterEventListener[];

  constructor() {
    this.buildConnectedAppsInBackground();
    this.hrDataUnRegisters = [];
  }

  public async getAppForOnboarding(): Promise<IAppForOnboarding | undefined> {
    const promises = allSupportedConnectsAppsInPriorityOrder.map((app) => app.isOnboardable());
    const appsToConsider = await pollyfilledAllSettled(promises);

    for (let index = 0; index < appsToConsider.length; index++) {
      const result = appsToConsider[index];
      const app = allSupportedConnectsAppsInPriorityOrder[index].name;

      if (result.status === 'rejected') {
        Logger.notify(new Error(`Failed to determine onboardable status for ${app}: ${result.reason}`));
      } else if (!result.value.isOnboardable) {
        Logger.debug(`ConnectedApps: ${app} is not onboardable`);
      } else {
        return {
          app,
          isConnectedDeviceAvailable: result.value.isConnectedDeviceAvailable,
          isAppInstalledOnConnectedDevice: result.value.isAppInstalledOnConnectedDevice,
          isAppInstalled: result.value.isAppInstalled,
        };
      }
    }

    Logger.notify(new Error(
      'No onboardable app found. '
      + 'This should only happen when nothing can be installed on the device',
    ));
    return undefined;
  }

  public async getAuthorizedApps(): Promise<ConnectedWorkoutApp[]> {
    const authorized: ConnectedWorkoutApp[] = [];
    for (const app of this.connectedApps) {
      const status = await app.authorizationStatus();
      if (status === AuthorizationStatus.authorized) {
        authorized.push(app);
      }
    }
    return authorized;
  }

  public async prepareHomeWorkout(options: IPrepareHomeWorkout): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.prepareHomeWorkout(options)));
  }

  public async prepareStudioWorkout(options: IPrepareStudioWorkout, listener: HRDataListener): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.prepareStudioWorkout(options)));
    await this.listenForHeartRateFor20Minutes(listener);
  }

  public async startWorkout(): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.startWorkout()));
  }

  public async publishWorkoutSessionToConnectedApps(workoutSession: UserWorkoutSession): Promise<void> {
    // todo: maybe rename this?
    await Promise.all(this.connectedApps.map((app) => app.stopAndSaveWorkout(workoutSession)));
  }

  public async stopAndDiscardWorkout(): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.stopAndDiscardWorkout()));
  }

  public async pauseWorkout(): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.pauseWorkout()));
  }

  public async resumeWorkout(): Promise<void> {
    await Promise.all(this.connectedApps.map((app) => app.resumeWorkout()));
  }

  public async canUseConnectedAppForHeartRate(): Promise<boolean> {
    const apps = await this.getConnectedAppsWithHeartRate();
    return apps.length > 0;
  }

  public getNumberOfConnectedAppsForHeartRate(): number {
    return this.hrDataUnRegisters.length;
  }

  public async startListeningForHeartRate(listener: HRDataListener) {
    await this.stopListenningForHeartRate();

    const apps = await this.getConnectedAppsWithHeartRate();
    this.hrDataUnRegisters = apps.map((app) => {
      const mappedListener = (heartRate: number) => {
        // todo: maybe each app should have this, especially if it provides timestamps
        listener({
          heartRate,
          timestamp: new Date(),
          source: app.name,
          debug: `ConnectedApp:${app.name}`,
        });
      };
      const unregister = app.registerHREventHandler(mappedListener);
      return unregister;
    });

    return this.hrDataUnRegisters.length;
  }

  public async stopListenningForHeartRate() {
    this.hrDataUnRegisters.forEach((unregister) => unregister());
    this.hrDataUnRegisters = [];
  }

  public async shouldRequestAuthorizationForInstalledApp() {
    for (const app of this.connectedApps) {
      const [isInstalled, authorizationStatus] = await Promise.all([
        app.isAppInstalledOnConnectedDevice(),
        app.authorizationStatus(),
      ]);
      const shouldRequestAuthorization = ![
        AuthorizationStatus.authorized,
        AuthorizationStatus.serviceUnavailable,
      ].includes(authorizationStatus);
      if (isInstalled && shouldRequestAuthorization) {
        return true;
      }
    }
    return false;
  }

  public getAppByName(name: string): ConnectedWorkoutApp | undefined {
    return this.connectedApps.find((app) => app.name === name);
  }

  private async listenForHeartRateFor20Minutes(listener: HRDataListener) {
    const useConnectedApps = await this.canUseConnectedAppForHeartRate();
    if (useConnectedApps) {
      this.startListeningForHeartRate(listener)
        .catch((e) => Logger.notify(e));
      const twentyMinutesInMs = 20 * 60 * 1000;
      setTimeout(this.stopListenningForHeartRate, twentyMinutesInMs);
    }
  }

  private async getConnectedAppsWithHeartRate(): Promise<ConnectedWorkoutApp[]> {
    // should we connect to all, or prefer a specific one?
    const appsAsHrMonitors: ConnectedWorkoutApp[] = [];
    for (const app of this.connectedApps) {
      const isHrMonitor = await app.canUseAsRealtimeHeartRateMonitor();
      if (isHrMonitor) {
        appsAsHrMonitors.push(app);
      }
    }
    return appsAsHrMonitors;
  }

  private buildConnectedAppsInBackground() {
    this.buildConnectedApps()
      .catch(() => { /* no-op */ });
  }

  private async buildConnectedApps() {
    const availability = await Promise.all(allSupportedConnectsAppsInPriorityOrder.map(initializeAppIfAvailable));
    availability.forEach((isAvailable, index) => {
      if (isAvailable) {
        this.connectedApps.push(allSupportedConnectsAppsInPriorityOrder[index]);
      }
    });
  }
}

export default new ConnectedAppsManagerSingleton();
